import { INTENSITY_MAX } from '../../utils/intensityScale';
import { BeerGuidelines } from './types';

export const PROFILE_AXES = [
  'bitterness',
  'hoppiness',
  'maltiness',
  'body',
  'esters',
  'sourness',
  'smokiness',
] as const;

export type ProfileAxis = (typeof PROFILE_AXES)[number];

/** Range on the 0-5 scale; 0..0 means the trait is not present in the style. */
export interface ProfileRange {
  min: number;
  max: number;
}

export type StyleProfile = Record<ProfileAxis, ProfileRange | null>;

export const PROFILE_SCALE_MAX = INTENSITY_MAX;

const capitalize = (axis: ProfileAxis) => axis.charAt(0).toUpperCase() + axis.slice(1);

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const clamp = (value: number) =>
  Math.min(PROFILE_SCALE_MAX, Math.max(0, Math.round(value)));

/**
 * Reads the profile of a style. Styles saved before ranges existed only have a single
 * value per axis; it stands for both ends of the range. Axes without any value are null.
 */
export const readProfile = (gl?: BeerGuidelines | null): StyleProfile => {
  const source = (gl ?? {}) as Record<string, unknown>;

  return PROFILE_AXES.reduce((profile, axis) => {
    const name = `profile${capitalize(axis)}`;
    const single = toNumber(source[name] ?? source[`profile_${axis}`]);
    const min = toNumber(source[`${name}Min`] ?? source[`profile_${axis}_min`]);
    const max = toNumber(source[`${name}Max`] ?? source[`profile_${axis}_max`]);

    const low = min ?? max ?? single;
    const high = max ?? min ?? single;

    if (low === null || high === null) {
      profile[axis] = null;
    } else {
      const [a, b] = [clamp(low), clamp(high)];
      profile[axis] = { min: Math.min(a, b), max: Math.max(a, b) };
    }
    return profile;
  }, {} as StyleProfile);
};

/** Guideline fields (camelCase, as the API client sends them) for a profile. */
export const profileToGuidelines = (profile: StyleProfile) =>
  PROFILE_AXES.reduce((fields, axis) => {
    const range = profile[axis];
    const name = `profile${capitalize(axis)}`;

    fields[`${name}Min`] = range ? range.min : null;
    fields[`${name}Max`] = range ? range.max : null;
    // Single value kept for clients that do not know ranges yet.
    fields[name] = range ? (range.min + range.max) / 2 : null;
    return fields;
  }, {} as Record<string, number | null>);

export const hasProfile = (profile: StyleProfile) =>
  PROFILE_AXES.some((axis) => profile[axis] !== null);

export const isAbsent = (range: ProfileRange) => range.min === 0 && range.max === 0;

/** Radar polygon points (in a 100x100 viewBox) for one end of the ranges. */
export const radarPoints = (
  profile: StyleProfile,
  end: 'min' | 'max',
  radius = 40,
): string =>
  PROFILE_AXES.map((axis, index) => {
    const value = profile[axis]?.[end] ?? 0;
    const angle = ((-90 + (360 / PROFILE_AXES.length) * index) * Math.PI) / 180;
    const r = (value / PROFILE_SCALE_MAX) * radius;
    return `${(50 + r * Math.cos(angle)).toFixed(1)},${(50 + r * Math.sin(angle)).toFixed(1)}`;
  }).join(' ');
